import { ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { distinctUntilChanged, first, skip, takeUntil } from 'rxjs/operators';
import swal from 'sweetalert2';

import { AuthService } from '../../auth/AuthService';
import {
  AuditSeverityCreatePayload,
  AuditSeverityTag,
  AuditSeverityUpdatePayload,
  QuestionsService
} from '../../Services/questions.service';

interface SeverityEditor {
  id: number | null;
  name: string;
  active: boolean;
  deductionPercent: number | null;
  failureCount: number | null;
}

@Component({
  selector: 'pm-severity-configuration',
  templateUrl: './severity-configuration.component.html',
  styleUrls: ['./severity-configuration.component.css']
})
export class SeverityConfigurationComponent implements OnInit, OnDestroy {
  severities: AuditSeverityTag[] = [];
  loading = false;
  saving = false;
  editorOpen = false;
  currentClientId: number | null = null;

  editor: SeverityEditor = this.emptyEditor();

  private destroyed = new Subject<void>();

  constructor(
    private questionsService: QuestionsService,
    public authService: AuthService,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.currentClientId = this.authService.getClientId() || null;
    this.loadSeverities();

    this.authService.clientId$
      .pipe(distinctUntilChanged(), skip(1), takeUntil(this.destroyed))
      .subscribe((clientId: number) => {
        this.ngZone.run(() => {
          this.currentClientId = clientId || null;
          this.severities = [];
          this.closeEditor();
          this.loadSeverities();
        });
      });
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }

  get canEdit(): boolean {
    return this.authService.hasRole('audit-edit');
  }

  get totalCount(): number {
    return this.severities.length;
  }

  get activeCount(): number {
    return this.severities.filter(item => item.active).length;
  }

  get automaticFailureCount(): number {
    return this.severities.filter(
      item => item.failureCount !== null && item.failureCount !== undefined
    ).length;
  }

  get noAutomaticFailureCount(): number {
    return this.severities.filter(
      item => item.failureCount === null || item.failureCount === undefined
    ).length;
  }

  loadSeverities(): void {
    if (!this.authService.getClientId()) {
      this.severities = [];
      return;
    }

    this.loading = true;

    this.questionsService.getSeverityTags()
      .pipe(first(), takeUntil(this.destroyed))
      .subscribe(
        (response: AuditSeverityTag[]) => {
          this.updateView(() => {
            this.severities = (response || [])
              .map(item => ({
                ...item,
                id: Number(item.id),
                clientId: Number(item.clientId),
                deductionPercent: Number(item.deductionPercent),
                failureCount:
                  item.failureCount === null || item.failureCount === undefined
                    ? null
                    : Number(item.failureCount)
              }))
              .sort((a, b) => a.name.localeCompare(b.name));

            this.loading = false;
          });
        },
        (error: any) => {
          this.updateView(() => {
            this.loading = false;
          });
          this.showError('Unable to Load Severities', error);
        }
      );
  }

  openCreate(): void {
    if (!this.canEdit) {
      return;
    }

    this.editor = this.emptyEditor();
    this.editorOpen = true;
  }

  openEdit(item: AuditSeverityTag): void {
    if (!this.canEdit) {
      return;
    }

    this.editor = {
      id: item.id,
      name: item.name,
      active: item.active,
      deductionPercent: item.deductionPercent,
      failureCount: item.failureCount
    };
    this.editorOpen = true;
  }

  closeEditor(): void {
    this.editorOpen = false;
    this.editor = this.emptyEditor();
    this.saving = false;
  }

  validateEditor(): string | null {
    const name = (this.editor.name || '').trim();

    if (!name) {
      return 'Severity name is required.';
    }

    if (name.length > 50) {
      return 'Severity name cannot exceed 50 characters.';
    }

    if (
      this.editor.deductionPercent === null ||
      this.editor.deductionPercent === undefined ||
      this.editor.deductionPercent === ('' as any)
    ) {
      return 'Deduction percentage is required.';
    }

    const deduction = Number(this.editor.deductionPercent);
    if (Number.isNaN(deduction) || deduction < 0 || deduction > 100) {
      return 'Deduction percentage must be between 0 and 100.';
    }

    if (
      this.editor.failureCount !== null &&
      this.editor.failureCount !== undefined &&
      this.editor.failureCount !== ('' as any)
    ) {
      const count = Number(this.editor.failureCount);
      if (!Number.isInteger(count) || count <= 0) {
        return 'Auto-Fail After Errors must be a positive whole number.';
      }
    }

    return null;
  }

  saveSeverity(): void {
    if (!this.canEdit || this.saving) {
      return;
    }

    const validation = this.validateEditor();
    if (validation) {
      swal.fire({
        icon: 'warning',
        title: 'Check Severity Configuration',
        text: validation
      });
      return;
    }

    this.saving = true;

    const failureCount =
      this.editor.failureCount === null ||
      this.editor.failureCount === undefined ||
      this.editor.failureCount === ('' as any)
        ? null
        : Number(this.editor.failureCount);

    if (this.editor.id === null) {
      const payload: AuditSeverityCreatePayload = {
        name: this.editor.name.trim(),
        deductionPercent: Number(this.editor.deductionPercent),
        failureCount
      };

      this.questionsService.createSeverityTag(payload)
        .pipe(first(), takeUntil(this.destroyed))
        .subscribe(
          () => this.handleSaveSuccess('Severity created successfully.'),
          (error: any) => this.handleSaveError(error)
        );

      return;
    }

    const payload: AuditSeverityUpdatePayload = {
      name: this.editor.name.trim(),
      active: this.editor.active,
      deductionPercent: Number(this.editor.deductionPercent),
      failureCount
    };

    this.questionsService.updateSeverityTag(this.editor.id, payload)
      .pipe(first(), takeUntil(this.destroyed))
      .subscribe(
        () => this.handleSaveSuccess('Severity updated successfully.'),
        (error: any) => this.handleSaveError(error)
      );
  }

  formatDeduction(item: AuditSeverityTag): string {
    return `${Number(item.deductionPercent || 0)}%`;
  }

  formatFailureCount(item: AuditSeverityTag): string {
    return item.failureCount === null || item.failureCount === undefined
      ? 'Never'
      : `${item.failureCount} error${item.failureCount === 1 ? '' : 's'}`;
  }

  trackBySeverityId(index: number, item: AuditSeverityTag): number {
    return item.id;
  }

  private emptyEditor(): SeverityEditor {
    return {
      id: null,
      name: '',
      active: true,
      deductionPercent: null,
      failureCount: null
    };
  }

  private handleSaveSuccess(message: string): void {
    this.updateView(() => {
      this.saving = false;
      this.editorOpen = false;
      this.editor = this.emptyEditor();
    });

    swal.fire({
      icon: 'success',
      title: 'Severity Configuration',
      text: message,
      showConfirmButton: false,
      timer: 1600
    });

    this.loadSeverities();
  }

  private handleSaveError(error: any): void {
    this.updateView(() => {
      this.saving = false;
    });

    this.showError(
      error?.status === 409 ? 'Severity Already Exists' : 'Unable to Save Severity',
      error
    );
  }

  private showError(title: string, error: any): void {
    swal.fire({
      icon: error?.status === 409 ? 'warning' : 'error',
      title,
      text:
        error?.error?.message ||
        error?.error?.detail ||
        error?.message ||
        'Something went wrong. Please try again.'
    });
  }

  private updateView(update: () => void): void {
    this.ngZone.run(() => {
      update();
      this.cdr.markForCheck();
      Promise.resolve().then(() => this.cdr.detectChanges());
    });
  }
}
